/**
 * Usage Status Widget Component
 * 
 * Compact widget for top navigation showing current usage status:
 * - Free usage counters
 * - Credit balances
 * - Quick access to purchase
 * - Visual indicators for low usage
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Search, Brain, Plus, Gift, Crown, AlertTriangle } from 'lucide-react';
import { useUsageTracking } from '../../hooks/useUsageTracking';
import { useAuth } from '@/contexts/AuthContext';

interface UsageStatusWidgetProps {
  onPurchaseClick?: () => void;
  className?: string;
}

export function UsageStatusWidget({ 
  onPurchaseClick, 
  className = "" 
}: UsageStatusWidgetProps) {
  const { userId } = useAuth();
  const { 
    usageSummary, 
    loading, 
    hasLowUsage, 
    isOutOfUsage, 
    isOwner 
  } = useUsageTracking(userId || '');

  if (loading || !usageSummary) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-8 h-4 bg-gray-200 rounded"></div>
          <div className="w-8 h-4 bg-gray-200 rounded"></div>
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Owner display
  if (isOwner) {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 px-2 py-1 bg-secondary rounded-md border border-border">
                <Crown className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">Owner</span>
                <Badge variant="secondary" className="text-xs">
                  Unlimited
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>You have unlimited access to all features</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <TooltipProvider>
        {/* Free Usage Indicators */}
        <div className="flex items-center gap-2">
          {/* Daily Searches */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-xs">
                <Gift className="w-3 h-3 text-green-600" />
                <span 
                  className={`font-medium ${
                    usageSummary.daily_searches_remaining <= 1 
                      ? 'text-red-600' 
                      : 'text-green-600'
                  }`}
                >
                  {usageSummary.daily_searches_remaining}/5
                </span>
                {usageSummary.daily_searches_remaining <= 1 && (
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                Free daily searches: {usageSummary.daily_searches_remaining} remaining
                {usageSummary.daily_searches_remaining <= 1 && (
                  <br />
                )}
                {usageSummary.daily_searches_remaining <= 1 && (
                  <span className="text-amber-300">Resets in ~14 hours</span>
                )}
              </p>
            </TooltipContent>
          </Tooltip>

          {/* Weekly AI Analysis */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-xs">
                <Brain className="w-3 h-3 text-green-600" />
                <span 
                  className={`font-medium ${
                    usageSummary.weekly_ai_analyses_remaining === 0 
                      ? 'text-red-600' 
                      : 'text-green-600'
                  }`}
                >
                  {usageSummary.weekly_ai_analyses_remaining}/1
                </span>
                {usageSummary.weekly_ai_analyses_remaining === 0 && (
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                Free weekly AI analysis: {usageSummary.weekly_ai_analyses_remaining} remaining
                {usageSummary.weekly_ai_analyses_remaining === 0 && (
                  <br />
                )}
                {usageSummary.weekly_ai_analyses_remaining === 0 && (
                  <span className="text-amber-300">Resets Monday</span>
                )}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Credit Indicators (if user has credits) */}
        {(usageSummary.search_credits > 0 || usageSummary.ai_analysis_credits > 0) && (
          <>
            <div className="w-px h-4 bg-gray-300" />
            <div className="flex items-center gap-2">
              {/* Search Credits */}
              {usageSummary.search_credits > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 text-xs">
                      <Search className="w-3 h-3 text-blue-600" />
                      <span className="font-medium text-blue-600">
                        {usageSummary.search_credits}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Search credits: {usageSummary.search_credits} available</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* AI Analysis Credits */}
              {usageSummary.ai_analysis_credits > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 text-xs">
                      <Brain className="w-3 h-3 text-blue-600" />
                      <span className="font-medium text-blue-600">
                        {usageSummary.ai_analysis_credits}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>AI analysis credits: {usageSummary.ai_analysis_credits} available</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </>
        )}

        {/* Warning Badge */}
        {isOutOfUsage && (
          <Badge variant="destructive" className="text-xs">
            No Usage
          </Badge>
        )}
        {hasLowUsage && !isOutOfUsage && (
          <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
            Low Usage
          </Badge>
        )}

        {/* Purchase Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              size="sm" 
              variant="outline" 
              className="h-6 px-2 text-xs"
              onClick={onPurchaseClick}
            >
              <Plus className="w-3 h-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Purchase credits</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

/**
 * Mobile-optimized version of the usage status widget
 */
export function UsageStatusWidgetMobile({ 
  onPurchaseClick, 
  className = "" 
}: UsageStatusWidgetProps) {
  const { userId } = useAuth();
  const { 
    usageSummary, 
    loading, 
    isOutOfUsage, 
    isOwner 
  } = useUsageTracking(userId || '');

  if (loading || !usageSummary) {
    return (
      <div className={`flex flex-col items-center gap-1 ${className}`}>
        <div className="animate-pulse">
          <div className="w-12 h-3 bg-gray-200 rounded mb-1"></div>
          <div className="w-8 h-3 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Owner display
  if (isOwner) {
    return (
      <div className={`flex flex-col items-center gap-1 text-center ${className}`}>
        <Crown className="w-4 h-4 text-primary" />
        <span className="text-xs font-medium text-foreground">Owner</span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center gap-1 text-center ${className}`}>
      {/* Free Usage Row */}
      <div className="flex items-center gap-2 text-xs">
        <div className="flex items-center gap-1">
          <Gift className="w-3 h-3 text-green-600" />
          <span 
            className={`font-medium ${
              usageSummary.daily_searches_remaining <= 1 ? 'text-red-600' : 'text-green-600'
            }`}
          >
            {usageSummary.daily_searches_remaining}/5
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Brain className="w-3 h-3 text-green-600" />
          <span 
            className={`font-medium ${
              usageSummary.weekly_ai_analyses_remaining === 0 ? 'text-red-600' : 'text-green-600'
            }`}
          >
            {usageSummary.weekly_ai_analyses_remaining}/1
          </span>
        </div>
      </div>

      {/* Credits Row (if available) */}
      {(usageSummary.search_credits > 0 || usageSummary.ai_analysis_credits > 0) && (
        <div className="flex items-center gap-2 text-xs">
          {usageSummary.search_credits > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-blue-600 font-medium">💳 {usageSummary.search_credits}</span>
            </div>
          )}
          {usageSummary.ai_analysis_credits > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-blue-600 font-medium">🔮 {usageSummary.ai_analysis_credits}</span>
            </div>
          )}
        </div>
      )}

      {/* Purchase Button */}
      <Button 
        size="sm" 
        variant="outline" 
        className="h-5 px-2 text-xs"
        onClick={onPurchaseClick}
      >
        <Plus className="w-3 h-3" />
      </Button>

      {/* Status indicator */}
      {isOutOfUsage && (
        <div className="text-xs text-red-600 font-medium">No Usage</div>
      )}
    </div>
  );
}